import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  Avatar,
  Box,
  useTheme,
  Skeleton,
} from '@mui/material';
import { Business } from '@mui/icons-material'; // Add this import for placeholder icon
import { PartnersTableDto } from '../../types/dataTypes';
import { partnerTableDefinition } from '../../utils/consts';
import { ExpandableChips } from '../Shared/ExpandableChips';
import { getImageSrc } from '../../utils/utilFunctions';

const SKELETON_ROW_COUNT = 8;

type PartnersTableProps = {
  items: PartnersTableDto[];
  isSelected: (projectNo: string) => boolean;
  partnerID : number;
  onDetailsClick: (partnerID: number) => void;
  onSelectChange: (partnerID: number) => void;
  loading?: boolean;
};

const PartnersTable = ({ items, isSelected, partnerID, onDetailsClick, onSelectChange, loading = false }: PartnersTableProps) => {
  const theme = useTheme();
  // Only show the full skeleton table before any data has arrived (initial load) - once
  // there's data on screen, a subsequent filter fetch shouldn't blank the table out.
  const showSkeleton = loading && items.length === 0;

  const renderLogo = (logoBase64: string | null) => {
    const imageSrc = getImageSrc(logoBase64);

    return (
        <Avatar
        src={imageSrc || undefined}
        sx={{ 
          width: 40, 
          height: 40,
          backgroundColor: imageSrc ? 'transparent' : 'grey.200',
          '& img': {
            objectFit: 'contain', // Use 'contain' to fit entire image, or 'cover' to fill circle
            width: '100%',
            height: '100%'
          }
        }}
      >
        {!imageSrc && <Business sx={{ color: 'grey.500' }} />}
      </Avatar>
    );
  };

  return (
    <Card>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {partnerTableDefinition.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    width: col.width,
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff',
                    padding: '10px',
                  }}  
                >
                  {col.label}
                </TableCell>
              ))}           
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {partnerTableDefinition.map((col) => (
                    <TableCell key={`skeleton-${col.key}`} sx={{ width: col.width }}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.map((item) => (
              <TableRow
                key={item.name}
                sx={{
                  backgroundColor: partnerID === item.id ? '#f0f0f0' : 'inherit',
                }}
              >
                {partnerTableDefinition.map((col, index) => {
                  // First column: Add logo
                  if (index === 0) {
                    return (
                      <TableCell key={col.key} sx={{ width: col.width }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {renderLogo(item.logo)}
                          {col.isList ? (
                            <ExpandableChips values={item[col.key] || []} maxVisible={3} />
                          ) : col.openDetails ? (
                            <span 
                              style={{ color: theme.palette.primary.main, cursor: 'pointer' }} 
                              onClick={() => onDetailsClick(item.partnerId)}
                            >
                              {item[col.key]}
                            </span>
                          ) : (
                            <span>{item[col.key]}</span>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }
                  
                  // Other columns: Existing logic
                  return col.isList ? (      
                    <TableCell key={col.key} sx={{ width: col.width }}>
                      <ExpandableChips values={item[col.key] || []} maxVisible={3} />
                    </TableCell>
                  ) : col.openDetails ? (
                    <TableCell 
                      key={col.key} 
                      sx={{ width: col.width, color: 'primary.main', cursor: 'pointer' }} 
                      onClick={() => onDetailsClick(item.partnerId)}
                    >
                      {item[col.key]}
                    </TableCell>
                  ) : (
                    <TableCell key={col.key} sx={{ width: col.width }}>
                      {item[col.key]}
                    </TableCell>
                  );
                })}                 
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default PartnersTable;